import { ReactNode } from "react"

import { useEntityType } from "@/hooks/useEntityType"
import { useFeatureFlag } from "@/hooks/useFeatureFlags"
import { useScope1Blocked } from "@/hooks/useScope1Blocked"
import routes from "@/routes/routes"

import { AccountingSetupLocked } from "./AccountingSetupLocked"
import { CommunicationHubComingSoon } from "./CommunicationHubComingSoon"
import { ReportsComingSoon } from "./ReportsComingSoon"
import { Scope1Blocked } from "./Scope1Blocked"
import { Scope1ComingSoon } from "./Scope1ComingSoon"
import { Scope2ComingSoon } from "./Scope2ComingSoon"
import { Scope2SetupLocked } from "./Scope2SetupLocked"

export type ComingSoonRoutesConfig = {
  /** Full pathname → Coming Soon screen */
  exact: Record<string, () => ReactNode>
  /** Show the same Coming Soon for this path and any subpath (e.g. /dashboard/campaign-manager/*) */
  prefix: Array<{ prefix: string; Component: () => ReactNode }>
  /**
   * Paths whose screen depends on a feature flag, whatever the flags say, and
   * that flag. Until the flag is known (loaded, or set by a dev override), these
   * must not be decided either way (MPE-1367).
   */
  flagGated: { exact: Record<string, string>; prefix: Array<{ prefix: string; flag: string }> }
}

const FLAG_GATED: ComingSoonRoutesConfig["flagGated"] = {
  exact: { [routes["dashboard/scope2"]]: "scope_2", [routes["dashboard/scope2/eac"]]: "scope_2" },
  prefix: [
    { prefix: routes["dashboard/impact"], flag: "communication_hub" },
    { prefix: routes["dashboard/content-studio"], flag: "communication_hub" },
    { prefix: routes["dashboard/campaign-manager"], flag: "communication_hub" },
  ],
}

// Hook returning routes that should show ComingSoon instead of the real page
export const useComingSoonRoutes = (): ComingSoonRoutesConfig => {
  const scope2Enabled = useFeatureFlag("scope_2")
  const communicationHubEnabled = useFeatureFlag("communication_hub")
  const { isBlocked: scope1Blocked } = useScope1Blocked()
  const entityType = useEntityType()
  // Scope 2 and Accounting share the Scope 1 lock; municipalities keep their current access (MPE-1537).
  const climateSetupLocked = scope1Blocked && entityType !== "municipality"

  return {
    exact: {
      ...(climateSetupLocked && {
        [routes["dashboard/scope2"]]: Scope2SetupLocked,
        [routes["dashboard/scope2/eac"]]: Scope2SetupLocked,
      }),
      // After the lock on purpose: while Scope 2 is not released, its Coming Soon screen wins.
      ...(!scope2Enabled && {
        [routes["dashboard/scope2"]]: Scope2ComingSoon,
        [routes["dashboard/scope2/eac"]]: Scope2ComingSoon,
      }),
      [routes["dashboard/reports"]]: ReportsComingSoon,
      // Climate setup only gates the intentional Climate overview.
      ...(scope1Blocked && {
        [routes.climate]: Scope1Blocked,
      }),
    },
    prefix: [
      // While setup is incomplete, all Scope 1 sub-pages show the locked screen
      ...(scope1Blocked ? [{ prefix: routes["dashboard/scope1"], Component: Scope1ComingSoon }] : []),
      ...(climateSetupLocked ? [{ prefix: routes["dashboard/accounting"], Component: AccountingSetupLocked }] : []),
      ...(!communicationHubEnabled
        ? [
            { prefix: routes["dashboard/impact"], Component: CommunicationHubComingSoon },
            { prefix: routes["dashboard/content-studio"], Component: CommunicationHubComingSoon },
            { prefix: routes["dashboard/campaign-manager"], Component: CommunicationHubComingSoon },
          ]
        : []),
    ],
    flagGated: FLAG_GATED,
    // Add more routes here as needed
  }
}
